import { Button, FormControl, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Tag, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { adminDeleteConnection, adminDeleteConnectionByIngredientCode, adminDeleteConnectionBySeqNo, adminDeleteConnectionBySeqNoOrIngredientCode, adminGetConnection, adminUpdateConnection } from "../../../api";
import { colors } from "../../../styles";
import { IAdminConnection } from "../../../types";

interface UpdateConnectionModalProps {
    isOpen : boolean;
    onClose : () => void;

    connectionId: string;

    page: number;
    size: number;
    isDisplay: string;
    searchKeyword: string | null;
}

export default function UpdateConnectionModal(props : UpdateConnectionModalProps) {

    /* useMemo */

    const [medicineId, keywordId] = useMemo<[string, string]>(() => {
        return props.connectionId.split("*") as [string, string];
    }, [props.connectionId]);

    /* query */

    const { data: connectionData, isLoading: isConnectionDataLoading } = useQuery<IAdminConnection>({
        queryKey: ['adminGetConnection', medicineId, keywordId],
        queryFn: () => adminGetConnection({ medicineId, keywordId }),
        enabled: (!!medicineId && !!keywordId && props.isOpen),
    });

    const seqNo = useMemo(() => connectionData?.medicine.seqNo ?? "", [connectionData]);
    const ingredientCode = useMemo(() => connectionData?.medicine.ingredientCode ?? "", [connectionData]);

    console.log("🔗 connectionData:", connectionData);

    /* form */

    interface IUpdateConnectionForm {
        weight: number;
    }

    const { register, handleSubmit, formState: { errors, isValid }, watch } = useForm<IUpdateConnectionForm>({
        mode: "onChange",
    });

    const isUpdateConnectionValid = useMemo(() => {

        if (!connectionData) return false;

        const inputValue = watch("weight");
        const originalValue = connectionData.weight;

        if (!isValid) return false;
        if (Number(inputValue) === originalValue || inputValue === undefined || inputValue === null) return false;

        return true;
    }, [isValid, watch("weight"), connectionData]);

    console.log("🔗 isUpdateConnectionValid:", isUpdateConnectionValid);

    /* onSubmit */

    const onSubmit = async (data: IUpdateConnectionForm) => {
        const weightToNumber = Number(data.weight);
        if (isNaN(weightToNumber)) return;
        await updateConnectionMutation.mutateAsync({ medicineId, keywordId, weight: weightToNumber });
    };

    const onDeleteConnection = async () => {
        await deleteConnectionMutation.mutateAsync({ medicineId, keywordId });
    };

    const onDeleteConnectionBySeqNoOrIngredientCode = async () => {
        await deleteConnectionBySeqNoOrIngredientCodeMutation.mutateAsync({ seqNo, ingredientCode, keywordId });
    };
    
    const onDeleteConnectionBySeqNo = async () => {
        await deleteConnectionBySeqNoMutation.mutateAsync({ seqNo, keywordId });
    };

    const onDeleteConnectionByIngredientCode = async () => {
        await deleteConnectionByIngredientCodeMutation.mutateAsync({ ingredientCode, keywordId });
    };

    /* mutation */

    const queryClient = useQueryClient();

    const toast = useToast();

    const updateConnectionMutation = useMutation({
        mutationFn: adminUpdateConnection,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection', medicineId, keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword', medicineId] });
            props.onClose();
        },
        onError: (error: any) => {
            toast({ 
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });  
        },
    });

    const deleteConnectionMutation = useMutation({
        mutationFn: adminDeleteConnection,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection', medicineId, keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword', medicineId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword', medicineId] });
            props.onClose();
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const deleteConnectionBySeqNoOrIngredientCodeMutation = useMutation({
        mutationFn: adminDeleteConnectionBySeqNoOrIngredientCode,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
            props.onClose();
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const deleteConnectionBySeqNoMutation = useMutation({
        mutationFn: adminDeleteConnectionBySeqNo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
            props.onClose();
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const deleteConnectionByIngredientCodeMutation = useMutation({
        mutationFn: adminDeleteConnectionByIngredientCode,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinesByKeyword', props.page, props.size, props.isDisplay, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicineByKeyword'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords'] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminAutoCompleteKeyword'] });
            props.onClose();
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */

    return (
        <Modal motionPreset="slideInBottom" size={"xl"} isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={colors.blue} fontSize="lg" fontWeight="bold">{`${connectionData?.medicine.nameKor} #${connectionData?.keyword.keyword}`}</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={5}>
                    <FormControl>
                        <FormLabel fontSize="xs" color={colors.blue}>
                            가중치 수정
                        </FormLabel>
                        <Input 
                            {...register("weight", { required: true, min: 1, max: 100 })}
                            type="number" 
                            placeholder="새로운 가중치 입력"
                            defaultValue={connectionData?.weight ?? undefined}
                        />
                        {errors.weight?.message ? <Text fontSize={12} color={colors.red} padding={2}>{errors.weight?.message}</Text> : null}
                        <Text fontSize={12} color={colors.g3} padding={2}>1 이상 100 이하로 입력해주세요.</Text>
                    </FormControl>
                    <Stack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"center"} gap={3}>
                        <Button 
                            onClick={handleSubmit(onSubmit)}
                            isLoading={updateConnectionMutation.isPending}
                            disabled={!isUpdateConnectionValid}
                            width={"100%"}
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            _hover={{ bg: colors.blue }}
                            boxShadow="sm"
                            marginBottom={10}
                        >
                            수정
                        </Button>

                        <Stack width={"100%"} padding={0}>
                            <Text fontSize="xs" fontWeight={500} color={colors.blue} paddingX={0}>연결된 태그들</Text>
                        </Stack>

                        <Stack 
                            width={"100%"} 
                            padding={0} 
                            display={"flex"} 
                            flexDirection={"row"} 
                            gap={2}
                            flexWrap={"wrap"}
                        >
                            {connectionData?.keyword.tags.map((tag) => (
                                <Tag 
                                    fontSize="md" 
                                    fontWeight={500} 
                                    paddingX={2}
                                    paddingY={1}
                                    borderRadius={10}
                                    borderWidth={1}
                                    borderColor={colors.blue}
                                    backgroundColor={colors.light}
                                    color={colors.blue}
                                    boxShadow="sm"
                                >
                                    {tag.tag}
                                </Tag>
                            ))}
                        </Stack>

                        <Stack width={"100%"} padding={0}>
                            <Text fontSize="xs" fontWeight={500} color={colors.blue} paddingX={0}>연결 상태 관리</Text>
                        </Stack>

                        <Button 
                            width={"100%"}
                            backgroundColor={colors.white}
                            borderColor={colors.apple}
                            color={colors.apple}
                            borderWidth={1}
                            borderRadius={10}
                            _hover={{ bg: colors.apple, color: colors.white }}
                            boxShadow="sm"
                            onClick={onDeleteConnectionBySeqNoOrIngredientCode}
                            isLoading={deleteConnectionBySeqNoOrIngredientCodeMutation.isPending}
                        >
                            seqNo - ingredientCode -
                        </Button>
                        <HStack width={"100%"} justifyContent={"center"} alignItems={"center"} gap={3}>
                            <Button
                                width={"50%"}
                                backgroundColor={colors.white}
                                borderWidth={1}
                                borderColor={colors.teal}
                                color={colors.teal}
                                borderRadius={10}
                                _hover={{ bg: colors.teal, color: colors.white }}
                                boxShadow="sm"
                                onClick={onDeleteConnectionBySeqNo}
                                isLoading={deleteConnectionBySeqNoMutation.isPending}
                            >
                                seqNo -
                            </Button>
                            <Button
                                width={"50%"}
                                backgroundColor={colors.white}
                                borderWidth={1}
                                borderColor={colors.link}
                                color={colors.link}
                                borderRadius={10}
                                _hover={{ bg: colors.link, color: colors.white }}
                                boxShadow="sm"
                                onClick={onDeleteConnectionByIngredientCode}
                                isLoading={deleteConnectionByIngredientCodeMutation.isPending}
                            >
                                ingredientCode -    
                            </Button>
                        </HStack>
                        <Button 
                            width={"100%"}
                            backgroundColor={colors.white}
                            color={colors.black}
                            borderWidth={1}
                            borderColor={colors.black}
                            borderRadius={10}
                            _hover={{ bg: colors.black, color: colors.white }}
                            boxShadow="sm"
                            onClick={onDeleteConnection}
                            isLoading={deleteConnectionMutation.isPending}
                        >
                            개별 연결 해제
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
        )
}